/**
 * YatraNest Nepal — Central Role Routing Utility
 * Single source of truth for all role-to-dashboard mappings.
 * Import from here everywhere — never duplicate this logic.
 */

export type CanonicalRole =
  | 'tourist' |'guide' |'hotel_owner' |'vehicle_owner' |'travel_agency' |'admin';

/** Normalize legacy/variant role strings to canonical values */
export function normalizeRole(role?: string | null): CanonicalRole {
  switch (role) {
    case 'guide': case 'local_guide':
      return 'guide';
    case 'hotel_owner': case 'hotel':
      return 'hotel_owner';
    case 'vehicle_owner': case 'vehicle':
      return 'vehicle_owner';
    // Both 'agency' (legacy DB enum) and 'travel_agency' (canonical) map to travel_agency
    case 'travel_agency': case 'agency':
      return 'travel_agency';
    case 'admin':
      return 'admin';
    case 'tourist':
    default:
      return 'tourist';
  }
}

/** Map a canonical (or legacy) role to its dashboard path */
export function getRoleDashboardPath(role?: string | null): string {
  const canonical = normalizeRole(role);
  switch (canonical) {
    case 'guide':          return '/guide/dashboard';
    case 'hotel_owner':    return '/hotel-owner/dashboard';
    case 'vehicle_owner':  return '/vehicle-owner/dashboard';
    case 'travel_agency':  return '/agency/dashboard';
    case 'admin':          return '/admin';
    case 'tourist':
    default:               return '/dashboard';
  }
}

/** Role-specific dashboard route prefixes for middleware protection */
export const ROLE_DASHBOARD_PREFIXES: Record<string, CanonicalRole> = {
  '/guide/dashboard':        'guide',
  '/hotel-owner/dashboard':  'hotel_owner',
  '/vehicle-owner/dashboard':'vehicle_owner',
  '/agency/dashboard':       'travel_agency',
  '/admin':                  'admin',
};

/** Check if a user with the given role is allowed to access the given path */
export function isAuthorizedForPath(role: string | null | undefined, pathname: string): boolean {
  const canonical = normalizeRole(role);
  for (const [prefix, requiredRole] of Object.entries(ROLE_DASHBOARD_PREFIXES)) {
    if (pathname.startsWith(prefix)) {
      return canonical === requiredRole;
    }
  }
  // Tourist /dashboard — only tourist and admin
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) {
    return canonical === 'tourist' || canonical === 'admin';
  }
  return true;
}
